import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { saveBlob } from "../../libs/video-store";

export enum QueueTypeEnum {
  EmptyTrashImage = "empty_trash_image",
  FullTrashImage = "full_trash_image",
  MtrDocument = "mtr_document",
  Collect = "collect",
}

export type QueueType =
  | "empty_trash_image"
  | "full_trash_image"
  | "mtr_document"
  | "collect";

export type Status = "pending" | "sending" | "failed" | "done" | "in_queue";

export interface QueueItem {
  id: string;
  retry?: number;
  status?: Status;
  progress?: number;
  queueType: QueueType;
  referenceName?: string;
  payload?: Object;
}

export interface AddQueuePayload extends QueueItem {
  blob: Blob;
}
export interface QueueState {
  items: QueueItem[];
}

const initialState: QueueState = {
  items: [],
};

export const addUploadThunk = createAsyncThunk(
  "uploads/addUpload",
  async ({ blob, ...params }: AddQueuePayload) => {
    const blobKey = `${params.id}/${params.queueType}`;
    await saveBlob(blobKey, blob);
    return params;
  }
);

const queueSlice = createSlice({
  name: "queue",
  initialState,
  reducers: {
    create: (state, action: PayloadAction<QueueItem>) => {
      const existingIndex = state.items.findIndex(
        (item) => item.id === `${action.payload.id}/${action.payload.queueType}`
      );

      const newItem = {
        id: `${action.payload.id}/${action.payload.queueType}`,
        queueType: action.payload.queueType,
        status: "pending" as Status,
        retry: 0,
        progress: 0,
        referenceName: action.payload.referenceName,
        payload: action.payload.payload,
      };

      if (existingIndex !== -1) {
        state.items[existingIndex] = newItem;
      } else {
        state.items.push(newItem);
      }
    },
    updateUploadProgress: (
      state,
      action: PayloadAction<{
        id: string;
        queueType: QueueType;
        progress: number;
      }>
    ) => {
      const { id, queueType, progress } = action.payload;
      const item = state.items.find(
        (u) => u.id === id && u.queueType === queueType
      );
      if (item) {
        item.progress = progress;
      }
    },
    markSending: (
      state,
      action: PayloadAction<{ id: string; queueType: QueueType }>
    ) => {
      const { id, queueType } = action.payload;
      const item = state.items.find(
        (u) => u.id === id && u.queueType === queueType
      );
      if (item) {
        item.status = "sending";
      }
    },
    updateAttempts: (
      state,
      action: PayloadAction<{
        id: string;
        queueType: QueueType;
        retries: number;
      }>
    ) => {
      const { id, queueType, retries } = action.payload;
      const item = state.items.find(
        (u) => u.id === id && u.queueType === queueType
      );
      if (item) {
        item.retry = retries;
      }
    },
    remove: (
      state,
      action: PayloadAction<{ id: string; queueType: QueueType }>
    ) => {
      const { id, queueType } = action.payload;
      state.items = state.items.filter(
        (u) => !(u.id === id && u.queueType === queueType)
      );
    },
    updateStatusToQueued: (state, action: PayloadAction<{ ids: string[] }>) => {
      state.items.forEach((item) => {
        if (action.payload.ids.includes(item.id) && item.status !== "done") {
          item.status = "in_queue";
        }
      });
    },
    updateAllToQueue: (state, _: PayloadAction<void>) => {
      state.items.forEach((item) => {
        if (item.status !== "done") {
          item.status = "in_queue";
        }
      });
    },
    updateStatus: (
      state,
      action: PayloadAction<{
        ids: string[];
        status: Status;
        progress?: number;
      }>
    ) => {
      state.items.forEach((item) => {
        if (action.payload.ids.includes(item.id)) {
          item.status = action.payload.status;
          if (action.payload.progress) {
            item.progress = action.payload.progress;
          }
        }
      });
    },
    resetAttempts: (state, action: PayloadAction<{ ids: string[] }>) => {
      state.items.forEach((item) => {
        if (action.payload.ids.includes(item.id)) {
          item.retry = 0;
        }
      });
    },
    resetStatusToPending: (state, _: PayloadAction<void>) => {
      state.items.forEach((item) => {
        if (item.status !== "done") {
          item.status = "pending";
          item.progress = 0;
        }
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(addUploadThunk.fulfilled, (state, action) => {
      state.items.push({
        id: `${action.payload.id}/${action.payload.queueType}`,
        queueType: action.payload.queueType,
        status: "pending",
        retry: 0,
        progress: 0,
        referenceName: action.payload.referenceName,
      });
    });
  },
});

export const QueueActions = queueSlice.actions;

export default queueSlice.reducer;
